/**
 * Query keys, fetchers and small helpers for the invoice on its PO (TASK-0236, INC-8).
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api/client';
import { importKeys, invalidateAfterReceive, poKeys } from '../api';
import type {
  InvoiceDetail,
  OpenOrderChoice,
  OpenOrderRefusal,
  OrderCandidateOut,
  OrderCandidatesResponse,
} from './types';

export const invoiceKeys = {
  all: ['vendor-invoices'] as const,
  detail: (id: number) => ['vendor-invoices', 'detail', id] as const,
  form: (poId: number, importId: number | null, mode: 'scan' | 'manual') =>
    ['vendor-invoices', 'form', poId, importId, mode] as const,
  candidates: (
    vendorId: number,
    importId: number | null,
    productIds: string,
    invoiceNumber: string
  ) =>
    [
      'vendor-invoices',
      'candidates',
      vendorId,
      importId,
      productIds,
      invoiceNumber,
    ] as const,
};

/** Everything an invoice recorded, voided or edited can change. */
export function invalidateAfterInvoice(qc: QueryClient) {
  invalidateAfterReceive(qc);
  void qc.invalidateQueries({ queryKey: [...invoiceKeys.all, 'detail'] });
  void qc.invalidateQueries({ queryKey: [...invoiceKeys.all, 'candidates'] });
  // Never refetch an open invoice form: the PO's invoice screen reads it again only through
  // Reload, and a form for an invoice just recorded answers 409 (already recorded). Dropped instead.
  qc.removeQueries({ queryKey: [...invoiceKeys.all, 'form'], type: 'inactive' });
  void qc.invalidateQueries({ queryKey: importKeys.all });
  void qc.invalidateQueries({ queryKey: poKeys.all });
}

/* ── Refusals ─────────────────────────────────────────────────────────────── */

/** The refusal code on an API error, if any. */
export function codeOf(error: unknown): string | null {
  return error instanceof ApiError ? (error.body?.code ?? null) : null;
}

/** The `details` of a coded refusal, typed by the caller. */
export function detailsOf<T>(error: unknown): T | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.body?.details;
  return details && typeof details === 'object' ? (details as unknown as T) : null;
}

/** The orders a 409 OPEN_ORDER_EXISTS named, else null. */
export function openOrdersFromError(error: unknown): OpenOrderRefusal | null {
  if (codeOf(error) !== 'OPEN_ORDER_EXISTS') return null;
  const details = detailsOf<OpenOrderRefusal>(error);
  if (!details || !Array.isArray(details.orders)) return null;
  return { orders: details.orders, preselectPoId: details.preselectPoId ?? null };
}

/** `{ invoiceId, poId }` of a 409 ALREADY_RECORDED_ON_ORDER, else null. */
export function recordedOnOrderFromError(
  error: unknown
): { invoiceId: number; poId: number; message: string } | null {
  if (codeOf(error) !== 'ALREADY_RECORDED_ON_ORDER') return null;
  const details = detailsOf<{ invoiceId: number; poId: number }>(error);
  if (!details) return null;
  return {
    invoiceId: details.invoiceId,
    poId: details.poId,
    message: error instanceof Error ? error.message : '',
  };
}

/* ── Candidates ("which PO?") ─────────────────────────────────────────────── */

const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/** `Oct 20` for an ISO instant or a `YYYY-MM-DD` day (read as a local day). */
export function shortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : dayFormat.format(d);
}

/**
 * One candidate in operator words: "PO #612 · ordered Oct 20 · 38 of 41 lines on it · 35 at the
 * ordered qty", or "PO #615 · checked in Oct 27, no invoice yet".
 */
export function candidateLabel(
  candidate: OpenOrderChoice | OrderCandidateOut,
  /** False for typed lines: their score has no quantities, so "at the ordered qty" says nothing. */
  quantities = true
): string {
  const parts = [`PO #${candidate.poId}`];
  if (candidate.kind === 'received_not_billed') {
    const when = shortDate(candidate.receivedAt);
    parts.push(
      when ? `checked in ${when}, no invoice yet` : 'checked in, no invoice yet'
    );
  } else {
    const sent = shortDate(candidate.sentAt);
    parts.push(sent ? `ordered ${sent}` : 'ordered');
    const expected = shortDate(candidate.expectedAt);
    if (expected) parts.push(`expected ${expected}`);
  }
  const score = candidate.score;
  if (score && score.invoiceLines > 0) {
    parts.push(`${score.onOrder} of ${score.invoiceLines} lines on it`);
    if (quantities) parts.push(`${score.atOrderedQty} at the ordered qty`);
  } else {
    parts.push(`${candidate.lineCount} ${candidate.lineCount === 1 ? 'line' : 'lines'}`);
  }
  return parts.join(' · ');
}

export function fetchOrderCandidates(params: {
  vendorId: number;
  importId?: number | null;
  productIds?: number[];
  invoiceNumber?: string | null;
}): Promise<OrderCandidatesResponse> {
  const ids = [...new Set(params.productIds ?? [])].sort((a, b) => a - b).slice(0, 400);
  return api.get<OrderCandidatesResponse>('/purchasing/order-candidates', {
    params: {
      vendorId: params.vendorId,
      importId: params.importId ?? undefined,
      productIds: ids.length > 0 ? ids.join(',') : undefined,
      invoiceNumber: params.invoiceNumber?.trim() || undefined,
    },
  });
}

/** The vendor's open and received-but-not-billed POs, ranked, for the "which PO?" step. */
export function useOrderCandidates({
  vendorId,
  importId = null,
  productIds = [],
  invoiceNumber = '',
  enabled = true,
}: {
  vendorId: number | null;
  importId?: number | null;
  productIds?: number[];
  invoiceNumber?: string;
  enabled?: boolean;
}) {
  const ids = [...new Set(productIds)].sort((a, b) => a - b);
  const csv = ids.join(',');
  const number = invoiceNumber.trim();
  return useQuery({
    queryKey: invoiceKeys.candidates(vendorId ?? 0, importId, csv, number),
    queryFn: () =>
      fetchOrderCandidates({
        vendorId: vendorId!,
        importId,
        productIds: ids,
        invoiceNumber: number,
      }),
    enabled: enabled && vendorId !== null,
    staleTime: 15_000,
    retry: false,
  });
}

export function fetchInvoiceDetail(id: number): Promise<InvoiceDetail> {
  return api.get<InvoiceDetail>(`/purchasing/invoices/${id}`);
}

/* ── Client request id and the unsent entry ───────────────────────────────── */

/** A v4 uuid for `clientRequestId`. */
export function newClientRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The localStorage key of a PO's unsent invoice entry (one per PO and file). */
export function invoiceDraftKey(poId: number, importId: number | null): string {
  return `kk.invoice-entry.${poId}.${importId ?? 'typed'}`;
}

/**
 * Read a saved entry. Browser storage is a per-viewer convenience: any failure (private window,
 * blocked storage, unreadable JSON) reads as "nothing saved".
 */
export function loadInvoiceDraft<T>(key: string): T | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function saveInvoiceDraft(key: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the entry simply is not kept.
  }
}

export function clearInvoiceDraft(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // Nothing to do.
  }
}
